import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  /**
   * `window.scrollY` captured when the dialog opens (body `position: fixed` zeroes scroll reads).
   * Used to restore scroll on browsers where `body.style.top` is missing or unreliable.
   * @type {number}
   */
  #openScrollY = 0;

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    // Freeze the body at its exact pre-dialog layout width. `width: 100%` can
    // expand into the scrollbar gutter after scroll lock is applied on Windows.
    document.body.style.width = `${document.documentElement.clientWidth}px`;
    const scrollY = window.scrollY;
    this.#openScrollY = scrollY;
    document.body.style.position = 'fixed';
    document.body.style.left = '0';
    document.body.style.right = 'auto';
    document.body.style.top = `-${scrollY}px`;

    dialog.showModal();
    this.dispatchEvent(new DialogOpenEvent());

    // Wait until the next tick to add the event listeners to avoid race condition
    // when `showDialog` is called within a click event listener.
    setTimeout(() => {
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    // Temporarily disable CSS smooth scrolling on <html>. The theme sets
    // `scroll-behavior: smooth` globally, and some browsers (notably Safari < 17.4) treat
    // `behavior: 'instant'` on `scrollTo` as `'auto'`, which then falls back to the CSS value.
    // That triggers a smooth-scroll animation here, firing many "scroll-down" scroll events
    // and leaving the sticky-up header hidden after the drawer closes.
    const htmlEl = document.documentElement;
    const previousScrollBehavior = htmlEl.style.scrollBehavior;
    htmlEl.style.scrollBehavior = 'auto';

    const topStyle = document.body.style.top;
    const parsedTop = parseInt(topStyle, 10);
    const restoreY = Number.isFinite(parsedTop)
      ? Math.max(0, -parsedTop)
      : Math.max(0, this.#openScrollY);

    // Close the dialog *before* restoring scroll. While open, `html[scroll-lock]` sets
    // `overflow: hidden`; restoring scroll with that still applied is unreliable on mobile Safari.
    dialog.close();
    dialog.classList.remove('dialog-closing');
    syncDocumentScrollLock();

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.top = '';

    window.scrollTo(0, restoreY);

    // Mobile WebKit often applies scroll position after layout; resync header on the next frames.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const header = document.querySelector('#header-component');
        if (header && typeof (/** @type {any} */ (header).syncScrollState) === 'function') {
          /** @type {any} */ (header).syncScrollState();
        }
        htmlEl.style.scrollBehavior = previousScrollBehavior;
      });
    });

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDialogElement || event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        syncDocumentScrollLock();
      }
    }
  },
  { capture: true }
);

/** Keeps scroll lock in sync when more than one lockable dialog is present. */
function syncDocumentScrollLock() {
  const hasOpenScrollLock = document.querySelector('dialog[scroll-lock][open], details[scroll-lock][open]');

  if (hasOpenScrollLock) {
    document.documentElement.setAttribute('scroll-lock', '');
    return;
  }

  document.documentElement.removeAttribute('scroll-lock');
}
